// Formatting shared by the CRM's booking page tabs and the contact card.

// The mockup calls his zone "Tel Aviv"; Intl calls it Jerusalem.
const CITY = { "Asia/Jerusalem": "Tel Aviv", "Asia/Tel_Aviv": "Tel Aviv" };

export function city(zone) {
  if (!zone) return "";
  return CITY[zone] || String(zone).split("/").pop().replace(/_/g, " ");
}

export function day(iso, zone) {
  return new Intl.DateTimeFormat("en-US", { timeZone: zone, weekday: "short", month: "short", day: "numeric" }).format(
    new Date(iso)
  );
}

export function hm(iso, zone) {
  return new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .format(new Date(iso))
    .replace(/^0(\d)/, "$1");
}

export function range(start, end, zone) {
  return `${hm(start, zone)}–${hm(end, zone)}`;
}

/** "Sep 24, 14:02" */
export function stamp(iso, zone) {
  return `${new Intl.DateTimeFormat("en-US", { timeZone: zone, month: "short", day: "numeric" }).format(new Date(iso))}, ${hm(
    iso,
    zone
  )}`;
}

/** "Sun, Oct 11" for a YYYY-MM-DD, read at noon so it never slips a day. */
export function dateOnly(ymd) {
  if (!ymd) return "";
  const key = String(ymd).slice(0, 10);
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" }).format(
    new Date(`${key}T12:00:00Z`)
  );
}

export function daysUntil(ymd, now = new Date()) {
  if (!ymd) return null;
  const target = new Date(`${String(ymd).slice(0, 10)}T12:00:00Z`).getTime();
  return Math.round((target - now.getTime()) / 86400000);
}

export const INTENT_CHIP = {
  plan: { label: "Plan", cls: "crm-bkchip--plan" },
  fix: { label: "Fix", cls: "crm-bkchip--fix" },
  fit: { label: "Fit", cls: "crm-bkchip--fit" },
};

export function AgendaChip({ intent }) {
  const chip = INTENT_CHIP[intent];
  if (!chip) return <span className="crm-bkchip crm-bkchip--none">Not chosen</span>;
  return <span className={`crm-bkchip ${chip.cls}`}>{chip.label}</span>;
}

export const pad2 = (n) => String(n).padStart(2, "0");

const CHECK_IN_WORDS = (atIso, cancelledIso) => {
  if (!atIso || !cancelledIso) return "";
  const days = Math.round((new Date(`${String(atIso).slice(0, 10)}T12:00:00Z`) - new Date(cancelledIso)) / 86400000);
  return days > 20 ? "in a month" : "in 2 weeks";
};

/*
 * One sentence per event, used by the Log and by a booking's history. `who` is
 * left out of the history (the panel already says who) and put in bold in the
 * Log.
 */
export function eventSentence(e, { zone, agendas, withWho = true }) {
  const d = e.detail || {};
  const who = withWho ? e.name || d.name || d.email || "Someone" : null;
  const whoCo = who ? `${who}${e.company ? ` (${e.company})` : ""}` : null;
  const at = (iso) => `${day(iso, zone)} · ${hm(iso, zone)}`;
  const label = (key) => (agendas && agendas[key] ? agendas[key].label : key);
  const lead = (text) => (withWho ? { who: whoCo, text } : { who: null, text: text.charAt(0).toUpperCase() + text.slice(1) });

  switch (e.kind) {
    case "booked":
      return lead(`booked ${at(d.start)} from ${d.personal ? "a personal link" : "the public page"}.`);
    case "moved":
      return lead(`moved ${at(d.from)} → ${at(d.to)}.`);
    case "agenda_changed":
      if (!d.to) return lead("cleared the agenda.");
      return lead(d.from ? `changed the agenda to "${label(d.to)}".` : `chose "${label(d.to)}".`);
    case "note_changed":
      return lead(d.length ? "added a note." : "removed their note.");
    case "guest_added":
      return lead(`added ${d.email || "a colleague"} to the call.`);
    case "guest_removed":
      return lead(`took ${d.email || "a colleague"} off the call.`);
    case "cancelled":
      return lead(
        `cancelled ${d.was ? at(d.was) : "the call"}. ${
          d.check_in_at ? `Check back in: ${dateOnly(d.check_in_at)} (${CHECK_IN_WORDS(d.check_in_at, e.created_at)}).` : "No check-in."
        }`
      );
    case "slot_taken":
      return lead(
        `tried ${d.start ? at(d.start) : "a time"}${d.during === "move" ? " while moving their call" : ""}. Someone else got it first.`
      );
    case "calendar_error": {
      const step = { create: "add the event", move: "move the event", cancel: "cancel the event", describe: "update the invite", add_guest: "add a guest", remove_guest: "remove a guest" }[
        d.step
      ];
      return lead(`hit a calendar error: Google refused to ${step || "write"}${d.status ? ` (${d.status})` : ""}.`);
    }
    default:
      return lead(e.kind.replace(/_/g, " "));
  }
}

/** The dim line under a diagnostic: what happened next. */
export function outcomeLine(e, zone) {
  if (!e.outcome) return null;
  if (e.outcome.booked) {
    const mins = Math.max(1, Math.round((e.outcome.after_seconds || 0) / 60));
    return `Diagnostic · they booked ${day(e.outcome.start, zone)} · ${hm(e.outcome.start, zone)} ${mins} min later`;
  }
  return "Diagnostic · no booking followed within the hour";
}

export const KIND_TAG = {
  booked: { label: "Booked", cls: "crm-bktag--booked" },
  moved: { label: "Moved", cls: "" },
  agenda_changed: { label: "Agenda", cls: "" },
  note_changed: { label: "Note", cls: "" },
  guest_added: { label: "Guest", cls: "" },
  guest_removed: { label: "Guest", cls: "" },
  cancelled: { label: "Cancelled", cls: "crm-bktag--muted" },
  slot_taken: { label: "Time taken", cls: "crm-bktag--diag" },
  calendar_error: { label: "Calendar error", cls: "crm-bktag--diag" },
};
